import { useState, useEffect, useRef } from "react"

interface UsePageVisibilityOptions {
  onVisibilityChange?: (isVisible: boolean) => void
}

interface UsePageVisibilityReturn {
  isVisible: boolean
  isHidden: boolean
}

/**
 * Hook to detect page visibility changes using the Page Visibility API.
 *
 * This is critical for mobile browsers (especially iOS Safari) which
 * throttle or pause requestAnimationFrame when the page is backgrounded.
 *
 * Note: deliberately does NOT listen to window focus/blur - on desktop,
 * blur fires when the window merely loses focus (DevTools, second monitor)
 * while the page is still fully visible, which would false-pause the game.
 */
export function usePageVisibility(
  options?: UsePageVisibilityOptions
): UsePageVisibilityReturn {
  // Keep the latest callback in a ref so the listener is subscribed exactly
  // once, even when callers pass a fresh inline closure every render
  const callbackRef = useRef(options?.onVisibilityChange)
  callbackRef.current = options?.onVisibilityChange

  const [isVisible, setIsVisible] = useState(() => {
    if (typeof document === "undefined") return true
    return !document.hidden
  })

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden
      setIsVisible(visible)
      callbackRef.current?.(visible)
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // pagehide fires on iOS Safari in cases where visibilitychange may not
    // (e.g. swiping to another app) - treat it as hidden
    const handlePageHide = () => {
      setIsVisible(false)
      callbackRef.current?.(false)
    }
    window.addEventListener("pagehide", handlePageHide)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("pagehide", handlePageHide)
    }
  }, [])

  return {
    isVisible,
    isHidden: !isVisible,
  }
}
